import { isPlatformBrowser } from "@angular/common";
import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { FieldType } from "@ngx-formly/core";
import { Subject } from "rxjs";

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: "astrobin-formly-field-turnstile",
  templateUrl: "./formly-field-turnstile.component.html",
  styleUrls: ["./formly-field-turnstile.component.scss"]
})
export class FormlyFieldTurnstileComponent extends FieldType implements OnInit, OnDestroy {
  @ViewChild("turnstileContainer", { static: true }) turnstileContainer: ElementRef;

  private readonly _destroyed$ = new Subject<void>();
  private _widgetId: string | null = null;
  private _scriptLoaded = false;
  private readonly _isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    super();
    this._isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (this._isBrowser) {
      this._loadTurnstileScript();
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();

    if (this._isBrowser && this._widgetId && window.turnstile) {
      window.turnstile.remove(this._widgetId);
    }
  }

  private _loadTurnstileScript(): void {
    if (!this._isBrowser) {
      return;
    }

    if (this._scriptLoaded || document.querySelector('script[src*="turnstile"]')) {
      this._scriptLoaded = true;
      this._initializeTurnstile();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this._scriptLoaded = true;
      this._initializeTurnstile();
    };
    document.head.appendChild(script);
  }

  private _initializeTurnstile(): void {
    if (!this._isBrowser) {
      return;
    }

    if (!window.turnstile || !this.turnstileContainer) {
      setTimeout(() => this._initializeTurnstile(), 100);
      return;
    }

    const siteKey = this.props.siteKey || this.field.templateOptions?.siteKey;
    if (!siteKey) {
      console.error("Turnstile site key is required");
      return;
    }

    this._widgetId = window.turnstile.render(this.turnstileContainer.nativeElement, {
      sitekey: siteKey,
      theme: "dark",
      callback: (token: string) => {
        this.formControl.setValue(token);
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
      },
      "error-callback": () => {
        this.formControl.setValue(null);
        this.formControl.setErrors({ captchaError: true });
      },
      "expired-callback": () => {
        this.formControl.setValue(null);
        this.formControl.setErrors({ captchaExpired: true });
      }
    });
  }

  reset(): void {
    if (this._isBrowser && this._widgetId && window.turnstile) {
      window.turnstile.reset(this._widgetId);
      this.formControl.setValue(null);
    }
  }
}
